import axios from 'axios';

export type PrinterStatus = {
  status: 'DISPONIVEL' | 'IMPRIMINDO' | 'OFFLINE' | 'DESCONHECIDO';
  progresso?: number;
  tempNozzle?: number;
  targetNozzle?: number;
  tempBed?: number;
  targetBed?: number;
  arquivo?: string;
  error?: string;
};

export class PrinterService {
  /**
   * Tenta conectar com a impressora diretamente do navegador.
   * Isso contorna o problema de o servidor backend não acessar IPs locais.
   */
  async testarConexao(ip: string, tipo: string): Promise<boolean> {
    const url = `http://${ip}/api/status`; // Tenta um endpoint comum
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      // FlashForge específico ou outros tipos
      if (tipo === 'FLASHFORGE') {
        // Tenta porta 8899 (comum em FlashForge) via fetch básico (pode falhar por CORS/Protocolo)
        // Se falhar, tentamos porta 8080 ou 80
        try {
          await fetch(`http://${ip}:8080/control`, { mode: 'no-cors', signal: controller.signal });
          return true; // Se não deu erro de rede, algo respondeu
        } catch (e) {
          await fetch(`http://${ip}/`, { mode: 'no-cors', signal: controller.signal });
          return true;
        }
      }

      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      console.error('Erro ao testar conexão local:', error);
      return false;
    }
  }

  /**
   * Obtém o status em tempo real via navegador.
   */
  async getStatus(ip: string, tipo: string): Promise<PrinterStatus> {
    if (tipo === 'FLASHFORGE') {
      return this.getFlashForgeStatus(ip);
    }
    
    // Fallback para outros tipos se o frontend puder acessar
    try {
      const response = await axios.get(`http://${ip}/api/status`, { timeout: 3000 });
      return response.data;
    } catch (error: any) {
      return { status: 'OFFLINE', error: error.message };
    }
  }

  private async getFlashForgeStatus(ip: string): Promise<PrinterStatus> {
    // Implementação simplificada para FlashForge via HTTP se disponível
    // Caso contrário, retorna um status genérico de "Conectado" se o IP responder
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      await fetch(`http://${ip}/`, { mode: 'no-cors', signal: controller.signal });
      clearTimeout(timeoutId);
      
      return {
        status: 'DISPONIVEL', // Assume disponível se o IP responder
        tempNozzle: 0,
        tempBed: 0,
        progresso: 0
      };
    } catch (error: any) {
      return { status: 'OFFLINE', error: 'Impressora não encontrada na rede local' };
    }
  }

  /**
   * Envia G-Code diretamente do frontend.
   */
  async enviarGCode(ip: string, gcode: string): Promise<boolean> {
    console.log(`Enviando G-Code para ${ip}:`, gcode);
    // Aqui seria implementado o protocolo específico (ex: POST para /api/print ou WebSocket)
    return true;
  }
}

export const printerService = new PrinterService();
